import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import LogoImage from '../images/claim-up-2.png';
import axios from 'axios';
import { baseAPI } from '../config';
import userStore from '../store/userStore';
import {
  ScaleIcon,
  PresentationChartLineIcon,
  UserCircleIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  SparklesIcon,
  ArrowUpTrayIcon,
  AdjustmentsHorizontalIcon,
  ChatBubbleLeftRightIcon,
  ChatBubbleBottomCenterTextIcon,
  ShieldCheckIcon,
  DocumentArrowUpIcon,
  EnvelopeIcon,
  Cog6ToothIcon,
  ClipboardDocumentListIcon,
  CurrencyEuroIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';
import { isPartnerRole } from '@claimup/shared/constants/roles';

/**
 * Las secciones del menú del cliente (company y su equipo). El admin y el
 * despacho siguen con el menú de siempre. Cada entrada va a la sección que dice
 * su ruta; lo que no esté aquí cae en «Día a día».
 *
 * Agrupado por para qué se entra (27/10/2026, docs/ux-cliente-ar-plan.md): lo de
 * cada día arriba; la cartera, los ajustes y los informes, debajo.
 */
export const CLIENT_SECTIONS = [
  {
    id: 'dia',
    label: 'Día a día',
    paths: [
      '/hoy',
      '/tasks',
      '/assignments',
      '/ar-review',
      '/call-session',
      '/management',
      '/expedients',
      '/debtors-with-expedients',
    ],
  },
  {
    id: 'cartera',
    label: 'Cartera',
    paths: ['/import-expedients', '/bulk-documents', '/payment-plans', '/unmatched-whatsapps', '/unmatched-emails'],
  },
  { id: 'ajustes', label: 'Ajustes', paths: ['/ar-arranque', '/plantillas', '/ar-exit-rules', '/profile'] },
  { id: 'informes', label: 'Informes', paths: ['/dashboard', '/ar-billing', '/audit'] },
];
export const sectionOf = path =>
  (CLIENT_SECTIONS.find(sec => sec.paths.some(p => path === p || path.startsWith(`${p}/`))) || CLIENT_SECTIONS[0]).id;
const CLIENT_ROLES = ['company', 'gestor', 'operator', 'client'];

const PARTNER_MENU_PATHS = ['/profile', '/expedients', '/tasks'];

function Sidebar({ sidebarOpen, setSidebarOpen }) {
  const token = localStorage.getItem('token');
  const { user } = userStore();
  const location = useLocation();
  const { pathname } = location;

  const trigger = useRef(null);
  const sidebar = useRef(null);

  const storedSidebarExpanded = localStorage.getItem('sidebar-expanded');
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded === null ? true : storedSidebarExpanded === 'true'
  );

  const [taskCount, setTaskCount] = useState(0);
  // Propuestas de la IA esperando visto bueno. Va en el menú, y no solo dentro
  // de la pestaña de revisión, porque un plan parado NO produce ningún error:
  // el agente simplemente deja de actuar, en silencio. Si el único sitio donde
  // se ve la cola es la propia cola, hay que entrar en ella para enterarse de
  // que está atascada.
  const [reviewCount, setReviewCount] = useState(0);

  // Referencia para el ResizeObserver
  const resizeObserver = useRef(null);

  useEffect(() => {
    const fetchUserTaskCount = async () => {
      try {
        const response = await axios.get(`${baseAPI}/annotations/user-task-count`, {
          params: { user_id: user.id, role: user.role },
          headers: { Authorization: `Bearer ${token}` },
        });
        setTaskCount(response.data.count);
      } catch (error) {
        console.error('Error fetching user task count:', error);
      }
    };
    fetchUserTaskCount();
  }, [user.id, user.role, token]);

  useEffect(() => {
    // Solo admin: el endpoint está detrás de requireAdmin y para el resto
    // devolvería 403 en cada refresco.
    if (user.role !== 'admin') return undefined;

    const fetchReviewCount = async () => {
      try {
        const { data } = await axios.get(`${baseAPI}/ai-agent/review-queue`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setReviewCount(data?.data?.pending || 0);
      } catch (error) {
        // Sin ruido: si falla, el contador se queda a 0 y la pestaña sigue
        // siendo la fuente buena. No merece un toast en cada refresco.
        setReviewCount(0);
      }
    };

    fetchReviewCount();
    // Dos minutos: suficiente para enterarse a tiempo sin machacar la API.
    const id = setInterval(fetchReviewCount, 120000);
    return () => clearInterval(id);
  }, [user.role, token]);

  // La revisión de la IA del cliente (AR). Solo existe si su empresa tiene AR
  // activado: quien lo decide es el servidor, y si contesta que no (403) la
  // entrada no sale. `null` = no hay revisión para este usuario.
  const [arReviewCount, setArReviewCount] = useState(null);
  // Si su empresa le deja ver la Facturación a este operador o gestor
  // (docs/facturacion-equipo-plan.md). Lo decide el servidor: si dice que no, 403.
  const [equipoVeFacturacion, setEquipoVeFacturacion] = useState(false);
  useEffect(() => {
    if (!['operator', 'gestor'].includes(user.role) || !user.parent_id) return;
    axios
      .get(`${baseAPI}/recovery-billing/companies/${user.parent_id}/available`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => setEquipoVeFacturacion(Boolean(data?.data && 'recovery' in data.data)))
      .catch(() => setEquipoVeFacturacion(false));
  }, [user.role, user.parent_id, token]);
  // Si ve los planes de pago (docs/planes-de-pago-fase-1-plan.md, §5): la empresa
  // con AR, y su gestor y operator si ella se lo deja. Lo decide el servidor: se
  // pregunta al listado, y un 403 esconde la entrada.
  const [veoPlanes, setVeoPlanes] = useState(false);
  useEffect(() => {
    if (!['company', 'gestor', 'operator'].includes(user.role)) return;
    axios
      .get(`${baseAPI}/payment-plans/agreements`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setVeoPlanes(true))
      .catch(() => setVeoPlanes(false));
  }, [user.role, token]);
  // Si la empresa tiene gestores. La revisión de la IA es del gestor: la empresa
  // solo la ve en su menú cuando no tiene ninguno, para que nada se quede parado.
  const [arTieneGestores, setArTieneGestores] = useState(null);
  // Lo que su empresa le deja hacer al gestor (06-reparto §4.5). Solo esconde
  // entradas: quien decide es el servidor. `null` = aún no se sabe → se enseña.
  const [gestorPermisos, setGestorPermisos] = useState(null);

  useEffect(() => {
    if (user.role !== 'gestor') return;
    axios
      .get(`${baseAPI}/team-permissions`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setGestorPermisos(data?.data?.gestor || null))
      .catch(() => setGestorPermisos(null));
  }, [user.role, token]);

  useEffect(() => {
    if (user.role !== 'company' && user.role !== 'gestor') return undefined;

    const fetchArReview = async () => {
      try {
        const { data } = await axios.get(`${baseAPI}/ar-review/review-queue`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const pending = data?.data?.pending;
        setArReviewCount(typeof pending === 'number' ? pending : null);
        setArTieneGestores(data?.data?.has_gestores ?? null);
      } catch (error) {
        setArReviewCount(null);
      }
    };

    fetchArReview();
    const id = setInterval(fetchArReview, 120000);
    return () => clearInterval(id);
  }, [user.role, token]);

  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !trigger.current) return;
      if (!sidebarOpen || sidebar.current.contains(target) || trigger.current.contains(target)) return;
      setSidebarOpen(false);
    };

    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };

    document.addEventListener('click', clickHandler);
    document.addEventListener('keydown', keyHandler);

    return () => {
      document.removeEventListener('click', clickHandler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [sidebarOpen, setSidebarOpen]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('sidebar-expanded', sidebarExpanded);
      const body = document.querySelector('body');
      if (body) {
        if (sidebarExpanded) {
          body.classList.add('sidebar-expanded');
        } else {
          body.classList.remove('sidebar-expanded');
        }
      }
    }

    return () => {
      const body = document.querySelector('body');
      if (body) {
        body.classList.remove('sidebar-expanded');
      }
    };
  }, [sidebarExpanded]);

  useEffect(() => {
    // Limpiar el ResizeObserver existente si hay uno
    if (resizeObserver.current) {
      resizeObserver.current.disconnect();
    }

    // Crear nuevo ResizeObserver solo si el sidebar está montado
    if (sidebar.current) {
      resizeObserver.current = new ResizeObserver(() => {
        // Manejar el resize solo si el componente está montado
        if (sidebar.current) {
          // Tu lógica de resize aquí
        }
      });

      // Empezar a observar
      resizeObserver.current.observe(sidebar.current);
    }

    // Cleanup
    return () => {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
      }
    };
  }, []); // Empty dependency array since we only need to set up the observer once

  const menuItems = [
    // Lo primero que ve el cliente: lo que tiene pendiente hoy (TodayPage).
    ...(CLIENT_ROLES.includes(user.role) ? [{ path: '/hoy', icon: <HomeIcon className='w-6 h-6' />, label: 'Hoy' }] : []),
    // El mismo enlace que el desplegable del nombre, arriba a la derecha, y con
    // la misma regla: el operador no tiene perfil propio que ver.
    ...(user.role !== 'operator'
      ? [
          {
            path: `/profile/${user.id}`,
            icon: <UserCircleIcon className='w-6 h-6' />,
            label: 'Mi Perfil',
          },
        ]
      : []),
    {
      path: '/expedients',
      icon: (
        <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
          />
        </svg>
      ),
      label: 'Reclamaciones',
    },
    {
      path: '/debtors-with-expedients',
      icon: (
        <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
          />
        </svg>
      ),
      label: 'Mis deudores',
    },
    {
      path: '/dashboard',
      icon: (
        <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
          />
        </svg>
      ),
      label: 'Dashboard',
    },
    {
      path: '/tasks',
      icon: (
        <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4'
          />
        </svg>
      ),
      label: `Tareas (${taskCount})`,
    },
    // La revisión de la IA de sus expedientes AR. El número son las propuestas
    // esperando: un plan parado no avisa de nada por su cuenta. La hace el
    // gestor: la empresa solo la ve si no tiene ninguno.
    ...(arReviewCount !== null && (user.role !== 'company' || arTieneGestores === false)
      ? [
          {
            path: '/ar-review',
            icon: <SparklesIcon className='w-6 h-6' />,
            label: `Revisión IA (${arReviewCount})`,
          },
        ]
      : []),
    // Su facturación: AR (tramos) y recobro (lo cobrado), cada pestaña si la
    // tiene (docs/facturacion-cliente-plan.md). Solo la company: es su factura.
    // Ya no depende de tener AR: un cliente de recobro también la ve. Su
    // operador o gestor, si la empresa se lo ha encendido.
    ...(user.role === 'company' || equipoVeFacturacion
      ? [
          {
            path: '/ar-billing',
            icon: <BanknotesIcon className='w-6 h-6' />,
            label: 'Facturación',
          },
        ]
      : []),
    ...(user.role === 'company' && arReviewCount !== null
      ? [
          // Su cartera AR la sube ella: AR es flujo mensual, y si cada carga
          // depende de ClaimUp, ClaimUp es el cuello de botella.
          {
            path: '/import-expedients',
            icon: <ArrowUpTrayIcon className='w-6 h-6' />,
            label: 'Importar expedientes',
          },
          // Sus PDF en bloque, cada uno a su expediente (docs/volcado-pdf-documentos-plan.md).
          {
            path: '/bulk-documents',
            icon: <DocumentArrowUpIcon className='w-6 h-6' />,
            label: 'Volcado de documentos',
          },
          // Las tareas de sus AR y su panel de control, como las ve su gestor.
          {
            path: '/assignments',
            icon: <ClipboardDocumentListIcon className='w-6 h-6' />,
            label: 'Gestiones',
          },
          // Qué pasa con lo que no se cobra: a recobro o de baja, solo.
          {
            path: '/ar-exit-rules',
            icon: <AdjustmentsHorizontalIcon className='w-6 h-6' />,
            label: 'Reglas de salida AR',
          },
          // Los WhatsApps que entran a sus números sin expediente: los asigna
          // ella (docs/ar-entrantes-sin-expediente-plan.md). El gestor ya los
          // tiene en su sección.
          {
            path: '/unmatched-whatsapps',
            icon: <ChatBubbleLeftRightIcon className='w-6 h-6' />,
            label: 'WhatsApps sin asignar',
          },
          // Y las respuestas por correo que no se han podido cruzar con un
          // expediente: el mismo gesto, por el otro canal. En AR el id del
          // expediente viaja en la dirección, así que aquí cae poca cosa — lo
          // que llegue al buzón genérico o con la dirección destrozada por un
          // reenvío. Ver docs/ar-correo-entrante-plan.md.
          {
            path: '/unmatched-emails',
            icon: <EnvelopeIcon className='w-6 h-6' />,
            label: 'Emails sin asignar',
          },
          // Lo que dice cada mensaje, por canal: WhatsApp (lo revisa ClaimUp
          // antes de Meta), correo y SMS (salen como los escribe ella).
          {
            path: '/plantillas',
            icon: <ChatBubbleBottomCenterTextIcon className='w-6 h-6' />,
            label: 'Plantillas',
          },
          // «Configuración»: aquí se crean los flujos de comunicación — qué sale,
          // cuándo y en qué orden, y qué revisa una persona (docs/ar-arranque-plan.md).
          // Los textos van en las plantillas.
          {
            path: '/ar-arranque',
            icon: <Cog6ToothIcon className='w-6 h-6' />,
            label: 'Configuración',
          },
        ]
      : []),
    // La auditoría: todo lo que se ha hecho en sus AR, y lo que falta por hacer
    // (docs/ar-auditoria-plan.md). La empresa con AR y su gestor (que solo
    // existe en empresas con AR).
    ...((user.role === 'company' && arReviewCount !== null) || user.role === 'gestor'
      ? [{ path: '/audit', icon: <ShieldCheckIcon className='w-6 h-6' />, label: 'Auditoría' }]
      : []),
    // Los planes de pago acordados de su cartera AR. La empresa, solo con AR.
    ...(veoPlanes && (user.role !== 'company' || arReviewCount !== null)
      ? [{ path: '/payment-plans', icon: <CalendarDaysIcon className='w-6 h-6' />, label: 'Planes de pago' }]
      : []),
  ];

  const adminItems = [
    {
      path: '/users',
      icon: (
        <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z'
          />
        </svg>
      ),
      label: 'Usuarios',
    },
    {
      path: '/partners',
      icon: <ScaleIcon className='w-6 h-6' />,
      label: 'Despachos',
    },
    {
      // Sustituye a los paneles de Metabase. Solo admin (sin `roles` = admin).
      path: '/portfolio-performance',
      icon: <PresentationChartLineIcon className='w-6 h-6' />,
      label: 'Rendimiento',
    },
    {
      // Los expedientes en «Plan de pagos» y si siguen pagando. Solo admin (sin `roles`).
      path: '/payment-plans',
      icon: <CalendarDaysIcon className='w-6 h-6' />,
      label: 'Planes de pago',
    },
    {
      // AR (euros gestionados de cada cliente) y recobro (lo cobrado y los
      // honorarios de cada uno). Solo admin (sin `roles`).
      path: '/ar-billing',
      icon: <BanknotesIcon className='w-6 h-6' />,
      label: 'Facturación',
    },
    {
      // Lo que se gasta en cada proveedor y lo que cuesta cada acción. Solo admin (sin `roles`).
      path: '/costs',
      icon: <CurrencyEuroIcon className='w-6 h-6' />,
      label: 'Costes',
    },
    {
      path: '/import-expedients',
      icon: (
        <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12'
          />
        </svg>
      ),
      label: 'Importar Expedientes',
    },
    // PDF en bloque a los expedientes de la empresa que elija (docs/volcado-pdf-documentos-plan.md).
    {
      path: '/bulk-documents',
      icon: <DocumentArrowUpIcon className='w-6 h-6' />,
      label: 'Volcado de documentos',
    },
    {
      path: '/management',
      icon: (
        <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z'
          />
        </svg>
      ),
      label: 'Gestión',
      roles: ['admin', 'gestor'],
    },
    {
      path: '/assignments',
      icon: (
        <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01'
          />
        </svg>
      ),
      label: 'Gestiones',
      roles: ['admin', 'gestor'],
    },
    {
      path: '/call-session',
      icon: (
        <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z'
          />
        </svg>
      ),
      label: 'Sesión de llamadas',
      // Solo gestor y admin: el marcador reclama de la cola por `gestorId`, así
      // que para una company la pantalla no tendría de dónde sacar deudores.
      roles: ['admin', 'gestor'],
    },
    {
      path: '/unmatched-emails',
      icon: (
        <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
          />
        </svg>
      ),
      label: 'Emails sin asignar',
    },
    {
      path: '/unmatched-whatsapps',
      icon: (
        <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z'
          />
        </svg>
      ),
      label: 'WhatsApps sin asignar',
      roles: ['admin', 'gestor'],
    },
    // El arranque AR: el admin lo mira empresa por empresa, el gestor el de la
    // suya. Lo cambia solo la empresa (docs/ar-arranque-plan.md).
    {
      path: '/ar-arranque',
      icon: <Cog6ToothIcon className='w-6 h-6' />,
      // El admin ya tiene «Configuración» (/settings): la suya lleva «AR».
      label: user.role === 'gestor' ? 'Configuración' : 'Configuración AR',
      roles: ['admin', 'gestor'],
    },
    // {
    //   path: '/focus-expedients',
    //   icon: (
    //     <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
    //       <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
    //       <path
    //         strokeLinecap='round'
    //         strokeLinejoin='round'
    //         strokeWidth='2'
    //         d='M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z'
    //       />
    //     </svg>
    //   ),
    //   label: 'Expedientes focus',
    // },
    {
      path: '/recovered-amounts',
      icon: (
        <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z'
          />
        </svg>
      ),
      label: 'Importes recuperados',
    },
    {
      path: '/ai-agent',
      icon: (
        <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714a2.25 2.25 0 00.659 1.591L19 14.5M14.25 3.104c.251.023.501.05.75.082M19 14.5l-1.5 4.5H6.5L5 14.5m14 0H5'
          />
        </svg>
      ),
      label: 'AI Agent',
      // Las propuestas esperando visto bueno. Es lo único del menú que
      // significa "hay trabajo parado por tu culpa".
      badge: reviewCount || null,
    },
    {
      path: '/settings',
      icon: (
        <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
          />
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
        </svg>
      ),
      label: 'Configuración',
    },
  ];

  return (
    <div className='flex h-screen'>
      {/* Sidebar backdrop (mobile only) */}
      <div
        className={`fixed inset-0 bg-gray-900 bg-opacity-30 z-40 lg:hidden lg:z-auto transition-opacity duration-200 ${
          sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden='true'
      ></div>

      {/* Sidebar */}
      <div
        id='sidebar'
        ref={sidebar}
        className={`flex flex-col absolute z-40 left-0 top-0 lg:static lg:left-auto lg:top-auto lg:translate-x-0 h-screen overflow-y-scroll lg:overflow-y-auto no-scrollbar shrink-0 bg-gray-900 p-4 transition-all duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-64'
        } ${sidebarExpanded ? 'w-64' : 'w-20'}`}
      >
        {/* Sidebar header */}
        <div className='flex justify-between items-center mb-10 pr-3 sm:px-2'>
          {/* Close button */}
          <button
            ref={trigger}
            className='lg:hidden text-gray-500 hover:text-gray-400 transition-colors duration-150'
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-controls='sidebar'
            aria-expanded={sidebarOpen}
          >
            <span className='sr-only'>Close sidebar</span>
            <svg className='w-6 h-6 fill-current' viewBox='0 0 24 24'>
              <path d='M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z' />
            </svg>
          </button>
          {/* Logo */}
          <NavLink to='/' className={`block ${sidebarExpanded ? '' : 'hidden'}`}>
            <img src={LogoImage} className='w-23 h-auto' alt='Claim up' />
          </NavLink>
        </div>

        {/* Links */}
        <div className='space-y-8'>
          {/* Pages group */}
          <div>
            <h3 className={`text-xs uppercase text-gray-500 font-semibold pl-3 ${sidebarExpanded ? '' : 'hidden'}`}>
              Menu
            </h3>
            {CLIENT_ROLES.includes(user.role) ? (
              <MenuPorSecciones
                items={[
                  ...menuItems,
                  // Lo del gestor que antes colgaba del bloque «Admin».
                  ...(user.role === 'gestor'
                    ? adminItems
                        .filter(item => (item.roles || ['admin']).includes('gestor'))
                        .filter(item => !(item.path === '/call-session' && gestorPermisos?.call === false))
                        .filter(item => !menuItems.some(m => m.path === item.path))
                    : []),
                ]}
                pathname={pathname}
                expanded={sidebarExpanded}
              />
            ) : (
              <ul className='mt-3 space-y-2'>
                {menuItems
                  // Un despacho colaborador solo tiene su perfil, Reclamaciones y Tareas.
                  .filter(item => !isPartnerRole(user.role) || PARTNER_MENU_PATHS.some(p => item.path.startsWith(p)))
                  .map(item => (
                    <li
                      key={item.path}
                      className={`px-3 py-2 rounded-lg transition-colors duration-150 ${
                        pathname.includes(item.path) ? 'bg-gray-800 ring-1 ring-gray-600' : 'hover:bg-gray-800'
                      }`}
                    >
                      <NavLink
                        to={item.path}
                        end
                        className={`flex items-center text-gray-200 hover:text-white transition-colors duration-150 focus:outline-none ${
                          sidebarExpanded ? 'justify-start' : 'justify-center'
                        }`}
                        title={!sidebarExpanded ? item.label : ''}
                      >
                        <span className='w-6 h-6 text-gray-400 flex-shrink-0'>{item.icon}</span>
                        {sidebarExpanded && <span className='text-sm font-medium ml-3'>{item.label}</span>}
                      </NavLink>
                    </li>
                  ))}

                {(user.role === 'admin' || user.role === 'gestor') && (
                  <>
                    {sidebarExpanded && user.role === 'admin' && (
                      <li className='px-3 py-2'>
                        <div className='text-xs uppercase text-gray-500 font-semibold'>Admin</div>
                      </li>
                    )}
                    {adminItems
                      .filter(item => (item.roles || ['admin']).includes(user.role))
                      // El gestor al que su empresa le ha quitado llamar no ve la sesión.
                      .filter(
                        item =>
                          !(item.path === '/call-session' && user.role === 'gestor' && gestorPermisos?.call === false)
                      )
                      .map(item => (
                        <li
                          key={item.path}
                          className={`px-3 py-2 rounded-lg transition-colors duration-150 ${
                            pathname.includes(item.path) ? 'bg-gray-800 ring-1 ring-gray-600' : 'hover:bg-gray-800'
                          }`}
                        >
                          <NavLink
                            to={item.path}
                            end
                            className={`flex items-center text-gray-200 hover:text-white transition-colors duration-150 ${
                              sidebarExpanded ? 'justify-start' : 'justify-center'
                            }`}
                            title={!sidebarExpanded ? `${item.label}${item.badge ? ` (${item.badge})` : ''}` : ''}
                          >
                            <span className='w-6 h-6 text-gray-400 flex-shrink-0 relative'>
                              {item.icon}
                              {/* Con el menú plegado no hay sitio para el número:
                              un punto basta para que se vea que hay algo. */}
                              {!sidebarExpanded && !!item.badge && (
                                <span className='absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500' />
                              )}
                            </span>
                            {sidebarExpanded && (
                              <span className='text-sm font-medium ml-3 flex items-center gap-2'>
                                {item.label}
                                {!!item.badge && (
                                  <span className='inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-semibold'>
                                    {item.badge}
                                  </span>
                                )}
                              </span>
                            )}
                          </NavLink>
                        </li>
                      ))}
                  </>
                )}
              </ul>
            )}
          </div>
        </div>

        {/* Expand / collapse button */}
        <div className='pt-3 flex justify-end mt-auto'>
          <div className={`${sidebarExpanded ? 'px-3' : 'px-0'} py-2`}>
            <button
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
              className='p-1.5 rounded-lg hover:bg-gray-800 transition-colors duration-150 w-full flex items-center justify-center'
              title={sidebarExpanded ? 'Colapsar sidebar' : 'Expandir sidebar'}
            >
              <span className='sr-only'>Expand / collapse sidebar</span>
              <svg
                className={`w-6 h-6 fill-current text-gray-400 transition-transform duration-200 ${
                  sidebarExpanded ? 'rotate-180' : 'rotate-0'
                }`}
                viewBox='0 0 24 24'
              >
                <path d='M19.586 11l-5-5L16 4.586 23.414 12 16 19.414 14.586 18l5-5H7v-2z' />
                <path className='text-gray-600' d='M3 23H1V1h2z' />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** El menú del cliente, en secciones con su título (CLIENT_SECTIONS). */
function MenuPorSecciones({ items, pathname, expanded }) {
  return (
    <div className='mt-3 space-y-4'>
      {CLIENT_SECTIONS.map(sec => {
        const suyas = items.filter(item => sectionOf(item.path) === sec.id);
        if (!suyas.length) return null;
        return (
          <div key={sec.id}>
            {expanded && (
              <h4 className='px-3 mb-1 text-[11px] uppercase tracking-wide text-gray-500 font-semibold'>{sec.label}</h4>
            )}
            <ul className='space-y-1' aria-label={sec.label}>
              {suyas.map(item => (
                <li
                  key={item.path}
                  className={`px-3 py-2 rounded-lg transition-colors duration-150 ${
                    pathname.includes(item.path) ? 'bg-gray-800 ring-1 ring-gray-600' : 'hover:bg-gray-800'
                  }`}
                >
                  <NavLink
                    to={item.path}
                    end
                    className={`flex items-center text-gray-200 hover:text-white transition-colors duration-150 focus:outline-none ${
                      expanded ? 'justify-start' : 'justify-center'
                    }`}
                    title={!expanded ? item.label : ''}
                  >
                    <span className='w-6 h-6 text-gray-400 flex-shrink-0'>{item.icon}</span>
                    {expanded && <span className='text-sm font-medium ml-3'>{item.label}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

export default Sidebar;
